import type { AiCustomPart, AiEntityListPartType, AiEntityPartType, AiMessagePart } from 'juneau';

/** Sanity bound for ids rendered into navigation targets. */
const MAX_ID_LENGTH = 100;

/** True when the id is a plausible navigation target (non-empty, bounded string). */
export function isValidEntityId(id: unknown): id is string {
  return typeof id === 'string' && id.length > 0 && id.length <= MAX_ID_LENGTH;
}

/** Runtime guard for untrusted message parts claiming to be entities. */
export function isEntityPart(part: AiMessagePart): part is AiEntityPartType {
  return (
    part.type === 'entity' &&
    typeof (part as AiEntityPartType).entity === 'object' &&
    (part as AiEntityPartType).entity !== null &&
    typeof (part as AiEntityPartType).entity.title === 'string'
  );
}

/** Runtime guard for untrusted message parts claiming to be entity lists. */
export function isEntityListPart(part: AiMessagePart): part is AiEntityListPartType {
  return part.type === 'entity-list' && Array.isArray((part as AiEntityListPartType).entities);
}

/** One validation issue as the collection card lists it. */
export interface IssueRowData {
  severity: string | null;
  code: string;
  message: string;
  path: string | null;
}

/** Every validation issue of one invoice, collapsed into a single expandable card. */
export interface IssueCollectionPartData extends AiCustomPart {
  type: 'issue-collection';
  invoiceNumber: string | null;
  errorCount?: number;
  warningCount?: number;
  /** Rows carried in this part — may be fewer than `total`. */
  shown: number;
  /** Issues found on the invoice, including any beyond the embedded rows. */
  total: number;
  issues: IssueRowData[];
}

/** Runtime guard for untrusted message parts claiming to be issue collections. */
export function isIssueCollectionPart(part: {
  type: string;
  [key: string]: unknown;
}): part is IssueCollectionPartData {
  return part.type === 'issue-collection' && Array.isArray(part.issues);
}
